
import { Component, Mixins, Prop, Ref, Watch } from "vue-property-decorator";
import VisibleOptions from "@/components/widgets/VisibleOptions.vue";
import mixin from "./mixin";
import { getModule } from "vuex-module-decorators";
import { OrderModule, SystemModule } from "@/store/modules/";
import * as modals from "@/lib/modals";
import { rawApi } from "@/lib/api";

const systemModule = getModule(SystemModule);
const orderModule = getModule(OrderModule);

interface DefaultNote {
  id: string | number;
  type: string;
  text: string;
  facilityViewable: boolean;
  customerViewable: boolean;
  isLegacy: boolean;
  failedSchedulingAttempt: boolean;
  method: string;
  result: string;
  resultText: string;
}

@Component({
  name: modals.addNoteModal,
  components: {
    VisibleOptions
  }
})
export default class AddNoteModal extends Mixins(mixin) {  
  defaultNote: DefaultNote = {
    type: "",
    text: "",
    facilityViewable: true,
    customerViewable: true,
    id: null,
    isLegacy: false,
    failedSchedulingAttempt: false,
    method: "",
    result: "",
    resultText: ""
  };
  methodOptions: Array<{ value: string; text: string }> = [
      { value: 'Call', text: 'Call' },
      { value: 'Text', text: 'Text' },
      { value: 'Email', text: 'Email' }
    ];

  resultOptions: Array<{ value: string; text: string }> = [];
  localNote: DefaultNote = null;
  validationMessage: string = "";

  saving: boolean = false;
  addWithShipWithOrders: boolean = true;

  @Ref() readonly noteBody!: HTMLInputElement;

  get existingNote() {
    return this.localNote.id !== null;
  }

  get deliveryForId() {
    if(!this.currentOrder)
      return 0;
      
    return this.currentOrder.deliveryForId ?? 0;
  }

  get dialogTitle() {
    return this.existingNote ? "Edit Note" : "Add Note";
  }

  get currentOrder() {
    return orderModule.currentOrder;
  }

  get currentOrderNumber() {
    return orderModule.currentOrder.number;
  }

  get modalData() {
    return systemModule.modalData;
  }

  get existNoteOrderHeaderNumber() {
    if (this.modalData.note) {
      return this.modalData.note.orderHeaderNumber;
    }
    return null;
  }

  get currentNoteVisibleTo() {
    if (this.localNote) {
      if (this.localNote.facilityViewable && this.localNote.customerViewable) return "0";
      else if (this.localNote.customerViewable) return "2";
      else if (this.localNote.facilityViewable) return "3";
      else return "1";
    }
    return "0";
  }
  
  get isValid() {
    if (this.localNote.text && this.localNote.text.trim() !== "") {
      if (this.localNote.failedSchedulingAttempt) {
        if (this.localNote.method && this.localNote.result) {
          if (this.localNote.result === "OTH" && !this.localNote.resultText && !this.localNote.resultText.trim()) {
            this.validationMessage = "Please provide text for 'Other' result.";
            return false; 
          }
          this.validationMessage = ""; 
          return true;
        }
        this.validationMessage = "Please select a method and result for the failed scheduling attempt.";
        return false;
      }
      this.validationMessage = ""; 
      return true; 
    } else {
      this.validationMessage = "Please enter a note text.";
      return false; 
    }
  }

  set currentNoteVisibleTo(val) {
    switch (val) {
      case "0":
        this.localNote.customerViewable = true;
        this.localNote.facilityViewable = true;
        break;
      case "2":
        this.localNote.customerViewable = true;
        this.localNote.facilityViewable = false;
        break;
      case "3":
        this.localNote.customerViewable = false;
        this.localNote.facilityViewable = true;
        break;
      default:
        this.localNote.customerViewable = false;
        this.localNote.facilityViewable = false;
        break;
    }
  }

  created() {
    this.localNote = JSON.parse(JSON.stringify(this.defaultNote));
    this.loadResultOptions();
  }

  async loadResultOptions() {
    const response = await rawApi.get('/failedSchedulingAttemptsCodes');
    this.resultOptions = response.data.map((item: any) => ({
      value: item.resultCode,
      text: item.resultCodeDescription
    }));
  }

  resetModal() {
    this.addWithShipWithOrders = true;
    this.localNote = JSON.parse(JSON.stringify(this.defaultNote));
  }

  onShown() {
    this.show();
    if (this.modalData.note) {
      let existing = this.modalData.note;

      this.localNote = { ...existing };
    }
    this.noteBody.focus();
  }  

  async saveNote() {
    this.saving = true;
    if (this.existingNote) {
      let note = {
        orderHeaderNumber: this.existNoteOrderHeaderNumber,
        text: this.localNote.text,
        facilityViewable: this.localNote.facilityViewable,
        customerViewable: this.localNote.customerViewable,
        id: this.localNote.id,
        isLegacy: this.localNote.isLegacy
      };

      let action = orderModule.editOrderNote;

      try {
        await action(note);

        this.addActionResult({
          number: note.orderHeaderNumber,
          success: true,
          message: `Note successfully modified.`
        });
      } catch (error) {
        this.addActionResult({
          number: note.orderHeaderNumber,
          success: false,
          message: error.response.data.message,
          errors:
            error.response.data && error.response.data.errors ? error.response.data.errors : null,
          errorMessages:
            error.response.data && error.response.data.errorMessages
              ? error.response.data.errorMessages
              : null
        });
      }
    } else {
      for (let index = 0; index < this.selectedItems.length; index++) {
        const selectedOrder = this.selectedItems[index];

        let note = {
          orderHeaderNumber: parseInt(selectedOrder.number),
          text: this.localNote.text,
          facilityViewable: this.localNote.facilityViewable,
          customerViewable: this.localNote.customerViewable,
          id: this.localNote.id,
          isLegacy: this.localNote.isLegacy,
          addWithShipWithOrders: !this.modalData.displayShipWithOrderCheckbox
            ? false
            : this.addWithShipWithOrders,
          failedSchedulingAttempt: this.localNote.failedSchedulingAttempt,
          method: this.localNote.method,
          result: this.localNote.result,
          resultText: this.localNote.resultText
        };

        let action = orderModule.createOrderNotes;

        try {
          console.log(note);
          await action(note);

          this.addActionResult({
            number: selectedOrder.number,
            success: true,
            message: `Note added to order ${selectedOrder.number}.`
          });
        } catch (error) {
          this.addActionResult({
            number: selectedOrder.number,
            success: false,
            message: error.response && error.response.data ? error.response.data.message : "",
            errors:
              error.response.data && error.response.data.errors ? error.response.data.errors : null,
            errorMessages:
              error.response.data && error.response.data.errorMessages
                ? error.response.data.errorMessages
                : null
          });
        }
      }
    }

    this.saving = false;
    this.hideModal();
  }

  changeNoteVisibleTo(value) {
    this.localNote.customerViewable = value.customerViewable;
    this.localNote.facilityViewable = value.facilityViewable;
  }
}
